/**
 * "View as", per browser tab.
 *
 * The server answers a request as the person a tab views as only when the
 * request carries that tab's signed grant (app/helpers/impersonation.py).
 * The grant lives in sessionStorage, which each tab has its own of, so one
 * Priori staff member can view as Bradley's coach in one tab, Tennessee's
 * in another, and themselves in a third (Ben, 2026-10-26). Until then the
 * view-as was in the session cookie, which every tab shares.
 *
 * Every request this tab makes must carry the grant, or it is answered as
 * the staff member: a read shows the wrong person's view, and a write — an
 * upload — goes through as them when the view-as tab is meant to be read
 * only. So it is added at the chokepoints, not by each caller:
 *
 *  - fetch and XMLHttpRequest, wrapped once at boot (installViewAs): every
 *    same-origin or API request gets the HEADER;
 *  - what the browser loads by itself (an iframe, a download link, a new
 *    tab) cannot send a header: those addresses go through withViewAs, and
 *    the grant rides the URL (GET only, server-side).
 *
 * A tab with no grant is the staff member. A new tab starts with none
 * unless the browser copies sessionStorage into it (opening a link with
 * window.open does; a typed address or a bookmark does not): it shows the
 * staff member until they choose someone in it.
 */
export const VIEW_AS_HEADER = 'X-Priori-View-As'
export const VIEW_AS_PARAM = 'view_as'
const KEY = 'priori.viewAs'

function store(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null   // storage blocked: no view-as in this tab
  }
}

/** This tab's grant, or null. */
export function viewAsGrant(): string | null {
  return store()?.getItem(KEY) ?? null
}

/** This tab starts (or, after a club switch, continues) viewing as someone. */
export function setViewAsGrant(grant: string): void {
  store()?.setItem(KEY, grant)
}

/** This tab is the staff member again. */
export function clearViewAsGrant(): void {
  store()?.removeItem(KEY)
}

/** A stop the server has not yet recorded, kept for this tab
 *  (components/shell/stopImpersonating). */
export const PENDING_STOP_KEY = 'priori.viewAs.pendingStop'

/** Signing out: this tab keeps no view-as and no queued stop. The server's
 *  sign-out has already ended every grant this person was issued
 *  (impersonation.end_all_on_sign_out); a grant left here would only be
 *  sent, refused, by whoever signs in next in this tab. */
export function clearViewAsOnSignOut(): void {
  store()?.removeItem(KEY)
  store()?.removeItem(PENDING_STOP_KEY)
}

/** Whether a request to `url` goes to our own API (same origin, or the
 *  configured API host). A third-party request never carries the grant. */
export function isOwnRequest(url: string, apiBase = ''): boolean {
  if (typeof window === 'undefined') return false
  let u: URL
  try {
    u = new URL(url, window.location.href)
  } catch {
    return false
  }
  if (u.origin === window.location.origin) return true
  if (!apiBase) return false
  try {
    return u.origin === new URL(apiBase, window.location.href).origin
  } catch {
    return false
  }
}

/** `url` with this tab's grant, for what the browser loads itself. The
 *  same URL when the tab views as no one, or the URL is not ours. */
export function withViewAs(url: string, apiBase = ''): string {
  const grant = viewAsGrant()
  if (!grant || !isOwnRequest(url, apiBase)) return url
  const hash = url.indexOf('#')
  const [head, frag] = hash < 0 ? [url, ''] : [url.slice(0, hash), url.slice(hash)]
  const sep = head.includes('?') ? '&' : '?'
  return `${head}${sep}${VIEW_AS_PARAM}=${encodeURIComponent(grant)}${frag}`
}

/** An APP PAGE's address with this tab's grant, in the fragment: the page
 *  adopts it on boot (adoptFromAddress) and strips it, and a fragment is
 *  never sent to a server — so no request log, proxy or referrer records
 *  it. For what our own code reads; a URL the server must read (an iframe,
 *  a download) takes withViewAs. */
export function pageWithViewAs(url: string): string {
  const grant = viewAsGrant()
  if (!grant || !isOwnRequest(url)) return url
  const hash = url.indexOf('#')
  const [head, frag] = hash < 0 ? [url, ''] : [url.slice(0, hash), url.slice(hash + 1)]
  const rest = withoutGrant(frag)
  return `${head}#${rest ? `${rest}&` : ''}${VIEW_AS_PARAM}=${encodeURIComponent(grant)}`
}

/** A fragment's parts, `&`-joined, less any view_as part — the rest kept
 *  VERBATIM. URLSearchParams would rewrite them: `#section` round-trips as
 *  `#section=` (SDET review of #931, 76.5), breaking an in-page anchor. */
function fragmentParts(frag: string): string[] {
  return frag ? frag.split('&') : []
}
function isGrantPart(part: string): boolean {
  return part.startsWith(`${VIEW_AS_PARAM}=`)
}
function withoutGrant(frag: string): string {
  return fragmentParts(frag).filter((p) => !isGrantPart(p)).join('&')
}

/** A page opened in a new tab or window with ?view_as= (withViewAs on an
 *  app address: a report, the match viewer) takes the grant as this tab's
 *  own, and the address loses it — a grant left in the bar is copied,
 *  bookmarked and shared. A grant that is not this person's is answered by
 *  the server as nobody's, so adopting one can never widen anything.
 *
 *  Also from the fragment (#view_as=): where the grant is only for this
 *  page's code, not for the server, it rides there so no request log ever
 *  records it (the legacy /game-match-summary redirect). */
export function adoptFromAddress(): void {
  const url = new URL(window.location.href)
  const frag = url.hash.slice(1)
  const inFrag = fragmentParts(frag).find(isGrantPart)
  const grant = url.searchParams.get(VIEW_AS_PARAM)
    ?? (inFrag ? decodeURIComponent(inFrag.slice(VIEW_AS_PARAM.length + 1)) : null)
  if (!grant) return
  setViewAsGrant(grant)
  url.searchParams.delete(VIEW_AS_PARAM)
  if (inFrag) {
    const rest = withoutGrant(frag)
    url.hash = rest ? `#${rest}` : ''
  }
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
}

let installed = false

/** Wrap fetch and XMLHttpRequest so every request of ours carries this
 *  tab's grant. Idempotent. */
export function installViewAs(apiBase = ''): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  adoptFromAddress()

  const realFetch = window.fetch.bind(window)
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const grant = viewAsGrant()
    const url = typeof input === 'string' ? input
      : input instanceof URL ? input.href : input.url
    if (!grant || !isOwnRequest(url, apiBase)) return realFetch(input, init)
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined))
    if (!headers.has(VIEW_AS_HEADER)) headers.set(VIEW_AS_HEADER, grant)
    return realFetch(input, { ...init, headers })
  }

  const proto = window.XMLHttpRequest?.prototype
  if (!proto) return
  const realOpen = proto.open
  const realSend = proto.send
  type Marked = XMLHttpRequest & { __prioriOwn?: boolean }
  proto.open = function (this: Marked, method: string, url: string | URL,
                         ...rest: unknown[]) {
    this.__prioriOwn = isOwnRequest(String(url), apiBase)
    return (realOpen as (...a: unknown[]) => void).call(this, method, url, ...rest)
  } as typeof proto.open
  proto.send = function (this: Marked, body?: Document | XMLHttpRequestBodyInit | null) {
    const grant = viewAsGrant()
    if (grant && this.__prioriOwn) this.setRequestHeader(VIEW_AS_HEADER, grant)
    return realSend.call(this, body)
  }
}

/** For tests: forget the wrapping (the wrapped functions stay wrapped). */
export function _resetInstalledForTests(): void {
  installed = false
}
